import Link from 'next/link';
import { Heart, MessageCircle, Clock, Eye } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { blogArticlePath, blogCategoryPath } from '../Router';
import type { BlogPostDocument } from '../../firebase/blog-db';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Eyebrow({ post }: { post: BlogPostDocument }) {
  return (
    <Link
      href={blogCategoryPath(post.categorySlug)}
      onClick={e => e.stopPropagation()}
      className="eyebrow text-hustlance-primary hover:text-hustlance-secondary transition-colors"
    >
      {post.categoryName}
    </Link>
  );
}

interface PostCardProps {
  post: BlogPostDocument;
  variant?: 'hero' | 'secondary' | 'default' | 'compact' | 'grouped' | 'listRow';
}

/** Views / comments / likes stats row — matches the reference's icon-row-under-a-divider pattern, used on cards inside category-grouped sections where the category itself is already the section heading. */
function StatsRow({ post }: { post: BlogPostDocument }) {
  return (
    <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200">
      <div className="flex items-center gap-4 text-gray-500 text-sm">
        <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" />{post.viewCount}</span>
        <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" />{post.commentCount}</span>
      </div>
      <span className="flex items-center gap-1.5 text-sm text-gray-700">
        {post.likeCount} <Heart className="w-4 h-4 text-gray-500" />
      </span>
    </div>
  );
}

export function PostCard({ post, variant = 'default' }: PostCardProps) {
  const hasImage = !!post.coverImageUrl;
  // Card-sized renders never need the full-resolution cover (only the
  // article page does) — prefer the small thumbnail when one was generated.
  const cardImageSrc = post.coverImageThumbUrl || post.coverImageUrl;

  if (variant === 'hero') {
    return (
      <Link href={blogArticlePath(post.slug)} className="group block">
        <div className={hasImage ? 'grid lg:grid-cols-2 gap-10 lg:gap-14 items-center' : 'max-w-2xl'}>
          {hasImage && (
            <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
              <ImageWithFallback
                src={cardImageSrc}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
            </div>
          )}
          <div>
            <div className="mb-5"><Eyebrow post={post} /></div>
            <h2 className="font-editorial text-4xl sm:text-5xl text-gray-900 leading-[1.05] mb-5 transition-colors group-hover:text-hustlance-primary">
              {post.title}
            </h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-7 line-clamp-3">{post.excerpt}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-400 mb-7 pb-7 border-b border-gray-100">
              <span className="font-medium text-gray-700">{post.authorName}</span>
              <span aria-hidden="true">·</span>
              <span>{formatDate(post.publishedAt)}</span>
              <span aria-hidden="true">·</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readingTimeMinutes} min read</span>
              <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{post.likeCount}</span>
              <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" />{post.commentCount}</span>
            </div>
            <span className="inline-flex items-center gap-2 text-sm font-bold tracking-wide uppercase text-gray-900 group-hover:gap-3.5 group-hover:text-hustlance-primary transition-all">
              Read Story <span aria-hidden="true">→</span>
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'secondary') {
    return (
      <Link href={blogArticlePath(post.slug)} className="group block h-full">
        <article className="h-full flex flex-col">
          {hasImage && (
            <div className="relative aspect-[3/2] overflow-hidden rounded-sm mb-5">
              <ImageWithFallback
                src={cardImageSrc}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              />
            </div>
          )}
          <div className="mb-3"><Eyebrow post={post} /></div>
          <h3 className="font-editorial text-2xl text-gray-900 leading-tight mb-3 transition-colors group-hover:text-hustlance-primary">
            {post.title}
          </h3>
          <p className="text-gray-500 leading-relaxed mb-4 flex-1 line-clamp-2">{post.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-auto">
            <span>{post.authorName}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(post.publishedAt)}</span>
            <span aria-hidden="true">·</span>
            <span>{post.readingTimeMinutes} min read</span>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'listRow') {
    return (
      <Link href={blogArticlePath(post.slug)} className="group block border border-gray-200 hover:border-gray-300 transition-colors">
        <div className={hasImage ? 'grid sm:grid-cols-2' : ''}>
          {hasImage && (
            <div className="relative aspect-[16/10] sm:aspect-auto overflow-hidden bg-gray-100">
              <ImageWithFallback
                src={cardImageSrc}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <div className="p-6 sm:p-8 flex flex-col">
            <div className="flex items-center gap-2.5 mb-4">
              <Avatar className="h-8 w-8">
                <AvatarImage src={post.authorAvatarUrl} alt={post.authorName} />
                <AvatarFallback className="bg-hustlance-primary text-white text-xs">
                  {post.authorName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-sm text-gray-500">
                <span className="text-gray-900 font-medium">{post.authorName}</span>
                <span className="mx-1.5" aria-hidden="true">·</span>
                {formatDate(post.publishedAt)}
                <span className="mx-1.5" aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readingTimeMinutes} min read</span>
              </div>
            </div>
            <h3 className="font-editorial text-2xl text-gray-900 leading-tight mb-3 transition-colors group-hover:text-hustlance-primary">
              {post.title}
            </h3>
            <p className="text-gray-500 leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
            <StatsRow post={post} />
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'grouped') {
    return (
      <Link href={blogArticlePath(post.slug)} className="group block h-full">
        <article className="h-full flex flex-col">
          {hasImage && (
            <div className="relative aspect-[16/10] overflow-hidden mb-5">
              <ImageWithFallback
                src={cardImageSrc}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <h3 className="font-editorial text-xl text-gray-900 leading-snug mb-2.5 line-clamp-2 transition-colors group-hover:text-hustlance-primary">
            {post.title}
          </h3>
          <p className="text-gray-500 text-[0.95rem] leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
          <StatsRow post={post} />
        </article>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link href={blogArticlePath(post.slug)} className="group flex gap-4 items-start">
        {hasImage && (
          <div className="relative w-28 h-20 flex-shrink-0 overflow-hidden rounded-sm">
            <ImageWithFallback
              src={cardImageSrc}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        )}
        <div className="min-w-0">
          <p className="eyebrow text-hustlance-primary mb-1">{post.categoryName}</p>
          <h3 className="font-editorial text-base text-gray-900 leading-snug line-clamp-2 transition-colors group-hover:text-hustlance-primary">
            {post.title}
          </h3>
          <p className="text-xs text-gray-400 mt-1.5">{formatDate(post.publishedAt)} · {post.readingTimeMinutes} min read</p>
        </div>
      </Link>
    );
  }

  return (
    <Link href={blogArticlePath(post.slug)} className="group block h-full">
      <article className="h-full flex flex-col">
        {hasImage && (
          <div className="relative aspect-[16/10] overflow-hidden rounded-sm mb-4">
            <ImageWithFallback
              src={cardImageSrc}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>
        )}
        <div className="mb-2.5"><Eyebrow post={post} /></div>
        <h3 className="font-editorial text-xl text-gray-900 leading-snug mb-2 line-clamp-2 transition-colors group-hover:text-hustlance-primary">
          {post.title}
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4 flex-1">{post.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-gray-400 mt-auto pt-3 border-t border-gray-100">
          <span>{post.authorName} · {formatDate(post.publishedAt)}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readingTimeMinutes}m</span>
            <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{post.likeCount}</span>
          </span>
        </div>
      </article>
    </Link>
  );
}
